import { useEffect, useState } from "react";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import { useTranslation } from "react-i18next";
import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import DeleteRecordButton from "../input/DeleteRecordButton";
import CustomRecordButton from "./../input/CustomRecordButton";

import AutoForm from "../form/functional/AutoForm";
import layoutConf from "../../config/layoutConf";
import BasicCrudScreenHeaderLayout from "../screenTypes/layout/BasicCrudScreenHeaderLayout";
import { cloneDeep, debounce, uniqBy } from "lodash";
import AutoFormViewLayout from "../form/layout/AutoFormViewLayout";
import useNotification from "../../hooks/useNotification";
import BasicTabScreenType from "../screenTypes/functional/BasicTabScreenType";

const id = "SalesOrderKONAM";
const mstTable = "MomSysUglOrderBuyerMst";
const detailTable = "MomSysUglOrderBuyerDetail";
const view = "VwSalesOrderAggregate";

function buildSchemaWithSpec(baseSchema, groupSpecs, groupNames) {
    const newSchema = cloneDeep(baseSchema);
    newSchema.useFormOrder = true;
    newSchema.useFormGroupName = true;
    newSchema.formGroups = groupNames;

    for (let column of newSchema.columns) {
        column.hideInForm = true;
        column.required = false;
        column.disabled = false;
    }

    groupSpecs.forEach((group, groupIndex) => {
        group.forEach((item, idx) => {
            const col = newSchema.columns.find((c) => c.name === item.name);
            if (col) {
                col.hideInForm = false;
                col.formGroup = groupIndex;
                col.formOrder = (idx + 1) * 10;
                Object.assign(col, item);
            }
        });
    });

    return newSchema;
}

function combineSchemas(mst, detail) {
    const clonedMst = cloneDeep(mst);
    const clonedDetail = cloneDeep(detail);

    const mstColumns = clonedMst?.columns || [];
    const detailColumns = clonedDetail?.columns || [];

    const combinedColumns = uniqBy([...mstColumns, ...detailColumns], (col) => col.name);

    return {
        name: "salesOrderAggregate",
        type: "BASE TABLE",
        autoIncrement: "orderBuyerDetailId",
        searchAll: false,
        keys: ["orderBuyerId", "orderBuyerDetailId"],
        columns: combinedColumns,
    };
}

function SalesOrderKONAM(props) {
    const { t } = useTranslation();

    const [cardSchema, setCardSchema] = useState();
    const [relIssueSchema, setRelIssueSchema] = useState();

    const [selectedSchema, setSelectedSchema] = useState();

    const [tab, setTab] = useState();
    const [api, setApi] = useState();
    const [grid, setGrid] = useState();
    const [selectGrid, setSelectGrid] = useState();
    const [height, setHeight] = useState(0);

    const { notifySuccess, notifyFailure, notifyWarning } = useNotification();

    function modifySchemaForCard(schema) {
        const groupNames = [
            "수주 공통 정보",
            "",
            "",
            "카드",
            "수량",
            "수량 납품 일자",
            "포장",
            "선인쇄물",
            "",
            "가격",
            "",
        ];

        const extraColumnsForUserInterface = [
            { name: "materialOption" },
            { name: "ManufacturingOrderStatus" },
            { name: "cardOption" },
            { name: "cardType" },
            { name: "cardName" },
            { name: "productCode" },
            { name: "itemUnitShippedQty" },
        ];

        schema.columns.push(...extraColumnsForUserInterface);

        const arr = [
            [{ name: "scmsYn" }, { name: "personize" }, { name: "urgency" }],
            [
                { name: "orderBuyerId", notInDialog: true },
                { name: "orderBuyerDate" },
                { name: "poDate" },
                { name: "dueDate" },
            ],
            [
                { name: "companyCode", width: "45%" },
                { name: "customerPoNo", width: "18%" },
                { name: "customerPoSeq", width: "1.5%", notInDialog: true },
                { name: "soProgress" },
                { name: "saleDuty" },
                { name: "registerDuty" },
                { name: "soType" },
                { name: "soProgressUpdateTime", notInDialog: true },
                { name: "ManufacturingOrderStatus", type: "varchar", notInDialog: true, width: "21.5%" },
            ],
            [
                { name: "materialCode", width: "45%" },

                { name: "materialOption", type: "varchar", notInDialog: true, width: "21.5%" },
                { name: "cardOption", type: "varchar", notInDialog: true, width: "21.5%" },

                { name: "cardType", type: "varchar", notInDialog: true, width: "21.5%" },
                { name: "cardName", type: "varchar", notInDialog: true, width: "21.5%" },
                { name: "issueOption" },
                { name: "productCode", type: "varchar", notInDialog: true, width: "21.5%" },
                { name: "cardDesign" },
                { name: "cardDirection" },
            ],
            [
                // 수량
                { name: "qty", width: "16.8%" },
                { name: "normSampleQty", width: "16.8%" },
                { name: "certiSampleQty", width: "16.8%" },
                { name: "issueAddQty", width: "16.8%" },
                { name: "itemUnitQty", width: "16.8%" },
                // 납품 수량
                { name: "qtyShip", width: "16.8%", notInDialog: true },
                { name: "normSampleQtyShipped", width: "16.8%", notInDialog: true },
                { name: "certiSampleQtyShipped", width: "16.8%", notInDialog: true },
                { name: "issueAddQtyShipped", width: "16.8%", notInDialog: true },
                {
                    name: "itemUnitShippedQty",
                    formItemType: "number",
                    type: "float",
                    width: "16.8%",
                    notInDialog: true,
                },
            ],
            // 납품 날짜
            [
                { name: "qtyMoved", width: "16.8%", notInDialog: true },
                { name: "qtyShipDate", width: "16.8%", notInDialog: true },
                { name: "normSampleShippedDate", width: "16.8%", notInDialog: true },
                { name: "certiSampleShippedDate", width: "16.8%", notInDialog: true },
                { name: "issueAddShippedDate", width: "16.8%", notInDialog: true },
            ],
            [
                { name: "slipSheetType", width: "45%" },
                { name: "bandingPacking", width: "45%" },
            ],
            [
                { name: "prePrintFrontQty", width: "45%" },
                { name: "prePrintRearQty", width: "45%" },
            ],
            [{ name: "engravingCost" }, { name: "engravingVatFlag" }, { name: "transCost" }, { name: "transVatFlag" }],
            [
                { name: "unitCost" },
                { name: "soAmount" },
                { name: "currency" },
                { name: "exchangeRate" },
                { name: "priceFlag" },
                { name: "netAmount" },
                { name: "vatType" },
                { name: "paymentMethod" },
                { name: "cobMaterialCode" },
                { name: "chipType" },
            ],

            [{ name: "remark", width: "100%" }],
        ];

        return buildSchemaWithSpec(schema, arr, groupNames);
    }

    function modifySchemaForRelIssue(schema) {
        const groupNames = ["", "", ""];
        const arr = [
            [
                { name: "orderBuyerId" },
                { name: "orderBuyerDate" },
                { name: "poDate" },
                { name: "dueDate" },
                { name: "urgency" },
                { name: "customerPoNo" },
                { name: "soProgressUpdateTime" },
                { name: "soProgress" },
                { name: "companyCode", width: "45%" },
                { name: "saleDuty" },
                { name: "soType" },
                { name: "ManufacturingOrderStatus", type: "varchar", notInDialog: true, width: "21.5%" },
            ],
            [
                { name: "materialCode", width: "45%" },
                { name: "materialOption", type: "varchar", notInDialog: true, width: "21.5%" },
            ],
            [
                { name: "qty" },
                { name: "qtyMoved" },
                { name: "qtyShip" },
                { name: "qtyShipDate" },

                { name: "unitCost", displayName: "relUnitCost" },
                { name: "soAmount" },
                { name: "currency" },
                { name: "priceFlag" },

                { name: "exchangeRate" },
                { name: "cobMaterialCode" },
                { name: "vatType" },
                { name: "paymentMethod" },
                { name: "remark", width: "92%" },
            ],
        ];

        return buildSchemaWithSpec(schema, arr, groupNames);
    }

    useEffect(() => {
        let mounted = true;

        window.addEventListener("resize", handleResize);
        handleResize();

        async function schemaSetting() {
            let mstSchema = await getSchema(mstTable);
            let detailSchema = await getSchema(detailTable);

            //combine schemas
            let combinedSchema = combineSchemas(mstSchema, detailSchema);

            let cardSchema = modifySchemaForCard(combinedSchema);

            let relIssueSchema = modifySchemaForRelIssue(combinedSchema);

            if (mounted) {
                setCardSchema(cardSchema);
                setRelIssueSchema(relIssueSchema);

                // setTab("card");
                // setSelectedSchema(cardSchema);

                setTab("relIssue");
                setSelectedSchema(relIssueSchema);
            }
        }

        schemaSetting();
        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        if (grid) {
            const handleGridSelection = debounce((grid) => {
                setSelectGrid(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            }, 10);

            grid.selectionChanged.addHandler(handleGridSelection);
            grid.itemsSourceChanged.addHandler(handleGridSelection);

            return () => {
                if (grid && !grid.isDestroyed) {
                    grid.selectionChanged.removeHandler(handleGridSelection);
                    grid.itemsSourceChanged.removeHandler(handleGridSelection);
                }
            };
        }
    }, [grid]);

    useEffect(() => {
        if (selectGrid && selectGrid.length > 0) {
            const flag = parseInt(selectGrid[0].issueOption) === 1;
            setTab(flag ? "relIssue" : "card");
            setSelectedSchema(flag ? relIssueSchema : cardSchema);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectGrid]);

    const onSuccess = (msg) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="vertical">
                <ReflexElement flex={0.3}>
                    <BasicCrudScreenType
                        id={id}
                        table={mstTable}
                        view={view}
                        headerTitle={t("term.SalesOrderKONAM")}
                        onApiInitialized={(api) => {
                            setApi(api);
                        }}
                        onInitialized={(grid) => {
                            grid.selectionMode = "Row";
                            setGrid(grid);
                        }}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                    />
                </ReflexElement>
                <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />
                <ReflexElement flex={0.7}>
                    <BasicCrudScreenHeaderLayout className="basicCrudScreenHeaderLayout">
                        <BasicTabScreenType
                            id={id}
                            tabs={["card", "relIssue"]}
                            tabLabels={["카드발급", "릴발급"]}
                            tabsStyle={{
                                left: 0,
                                margin: "0px",
                                padding: "0px",
                                pointerEvents: "none",
                            }}
                            value={tab}
                            rightGridItems={[
                                <BasicCrudScreenActionItemLayout>
                                    <CustomRecordButton
                                        id={id}
                                        grid={grid}
                                        refresh={api}
                                        schema={selectedSchema}
                                        onSuccess={onSuccess}
                                        disabled={Number(selectGrid?.isExist)}
                                        mode="edit"
                                        title="create"
                                        buttonFullName="buttonName.create"
                                        isFormData
                                        modifyFormDataCallbackOK={(formData) => {
                                            formData.orderBuyerId = "so";
                                            return formData;
                                        }}
                                    />
                                </BasicCrudScreenActionItemLayout>,
                                <BasicCrudScreenActionItemLayout>
                                    <CustomRecordButton
                                        id={id}
                                        grid={grid}
                                        refresh={api}
                                        schema={selectedSchema}
                                        isFormData
                                        disabled={!Number(selectGrid?.isExist)}
                                        mode="edit"
                                        method="put"
                                        title="update"
                                        buttonFullName="buttonName.update"
                                        onSuccess={notifySuccess}
                                        modifyUrlCallback={(url) =>
                                            url + "/" + selectGrid.materialCode + "/" + selectGrid.materialCodeVer
                                        }
                                        modifyFormDataCallback={(formData, name) => {
                                            if (name === "inlay") {
                                                formData.bumpingType = null;
                                                formData.spotType = null;
                                            }
                                        }}
                                    />
                                </BasicCrudScreenActionItemLayout>,
                                <BasicCrudScreenActionItemLayout>
                                    <DeleteRecordButton
                                        id={id}
                                        schema={selectedSchema}
                                        grid={grid}
                                        refresh={api}
                                        color="secondary"
                                        onSuccess={onSuccess}
                                        onFailure={onFailure}
                                        onPartialFailure={onNotifyWarning}
                                        disabled={!Number(selectGrid?.isExist)}
                                    />
                                </BasicCrudScreenActionItemLayout>,
                            ]}
                        />
                        <AutoFormViewLayout>
                            <AutoForm
                                schema={selectedSchema}
                                mode="edit"
                                initialData={{ ...selectGrid }}
                                onChange={() => {}}
                                checkDisabledCallback={(_, column) => {
                                    column.required = false;
                                    return true;
                                }}
                            />
                        </AutoFormViewLayout>
                    </BasicCrudScreenHeaderLayout>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default SalesOrderKONAM;
